import Button from "@/atoms/Button";
import Input from "@/atoms/Input";
import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { landlordsPropertySchema } from "@/helpers/validators";
import { LandlordsPropertyForm } from "./types";
import Close from "@/public/icons/close-simple-icon.svg";
import { AxiosError } from "axios";
import { useMutation } from "react-query";
import { landlordPropertyToMonday } from "@/services/api";
import Radio from "@/atoms/Radio";
import DashboardButton from "@/atoms/DashboardButton";
import { Props } from "./types";

const LandlordPropertyModal = ({
  setShowModal,
  landlordMondayID,
  setPopupIndex,
  setIsAdditionalProperty,
  email,
}: Props) => {
  const [isMutating, setIsMutating] = useState(false);
  const [showThanksMessage, setShowThanksMessage] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    getValues,
    reset,
  } = useForm<LandlordsPropertyForm>({
    resolver: yupResolver(landlordsPropertySchema),
    mode: "onTouched",
  });

  const { mutate } = useMutation(landlordPropertyToMonday, {
    onSuccess: () => {
      setShowThanksMessage(true);
      setPopupIndex(2);
      setIsAdditionalProperty(false);
    },
    onError: (err: AxiosError) => {
      console.log("Error:", err);
    },
    onMutate: () => setIsMutating(true),
    onSettled: () => setIsMutating(false),
  });

  const handleAddAnotherProperty = () => {
    setPopupIndex(1);
    setShowThanksMessage(false);
    setIsAdditionalProperty(true);
    reset({
      street_address: "",
      post_code: "",
      number_of_bedrooms: undefined,
    });
  };

  const handleCloseAndResetModal = () => {
    setShowModal(false);
    setPopupIndex(0);
  };

  const onSubmit = async (data: any) => {
    mutate({
      landlordMondayId: landlordMondayID,
      data: {
        street_address: data.street_address,
        post_code: data.post_code,
        number_of_bedrooms: data.number_of_bedrooms,
      },
    });
  };

  return (
    <>
      <div>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="flex flex-col mx-auto mb-8">
            {showThanksMessage ? (
              <div className="flex flex-col md:flex-row justify-between bg-white my-8 items-center align-middle form-container !p-10">
                <div>Do you have more than one property?</div>

                <div className="flex w-full md:w-auto" onClick={handleAddAnotherProperty}>
                  <DashboardButton className="w-full md:w-auto leading-6 bg-landing-orange border-landing-orange mt-8 md:mt-0 text-white">
                    Add a property
                  </DashboardButton>
                </div>
              </div>
            ) : (
              <div className="form-container !px-8">
                {email && <Input type="text" label="Email" value={email} disabled />}
                <Input
                  type="text"
                  label="Street address"
                  placeholder="e.g. 251 Menlove Avenue"
                  {...register("street_address")}
                  success={!!getValues("street_address") && !errors?.street_address}
                  error={errors.street_address && errors.street_address.message}
                />
                <div>
                  <Input
                    type="text"
                    label="Post code"
                    placeholder="e.g. L25 7SA"
                    {...register("post_code")}
                    success={!!getValues("post_code") && !errors?.post_code}
                    error={errors.post_code && errors.post_code.message}
                  />
                </div>
                <div>
                  <div>
                    <div className="text-lg font-bold ">Number of bedrooms</div>
                    <Radio
                      options={[
                        { value: "STUDIO", caption: "Studio" },
                        { value: "ONE", caption: "1" },
                        { value: "TWO", caption: "2" },
                        { value: "THREE", caption: "3" },
                        { value: "FOUR", caption: "4" },
                        { value: "FIVE_OR_MORE", caption: "5+" },
                      ]}
                      {...register("number_of_bedrooms", { required: true })}
                      error={errors.number_of_bedrooms && errors.number_of_bedrooms.message}
                      className="flex-1"
                      lineBreakIndex={2}
                    />
                  </div>
                </div>
                <div className="flex flex-row justify-end mt-8">
                  <Button
                    type="submit"
                    isLoading={isMutating}
                    className="flex justify-center items-center px-5 text-md md:text-lg font-bold border rounded text-white h-12 md:h-14 border-grey-400 leading-snug disabled:border-2 disabled:text-grey-650 disabled:border-grey-650"
                  >
                    Get my offer
                  </Button>
                </div>
              </div>
            )}
          </div>
        </form>
        <div className="h-[1px] border-b-[1px] border-landing-darkBlue mb-8 "></div>
        <div className="flex flex-col md:flex-row justify-between bg-white my-8 items-center align-middle form-container !p-10">
          <div>Do you want to know more?</div>

          <div className="flex w-full md:w-auto">
            <a
              href="https://meetings-eu1.hubspot.com/adam-purcell"
              target="_blank"
              rel="noreferrer"
              className="flex items-center w-full"
            >
              <DashboardButton
                className="w-full md:w-auto leading-6 bg-landing-denim border-landing-denim rounded mt-8 md:mt-0 text-white"
                suffix="/icons/phone_white.svg"
              >
                Schedule a call
              </DashboardButton>
            </a>
          </div>
        </div>
        <div className="flex flex-row justify-center my-8 mx-auto items-center text-center w-full">
          <Button
            onClick={handleCloseAndResetModal}
            variant={"outline"}
            suffix={<Close />}
            className="rounded"
          >
            Close
          </Button>
        </div>
      </div>
    </>
  );
};

export default LandlordPropertyModal;
